import {
  ButtonERP,
  ButtonPrimeReact,
  OptionsMenuStorybook,
  PrimeReactModal,
  TableERP,
} from "@finanedi.es/storybook"
import { IconName } from "@fortawesome/fontawesome-svg-core"
import { faLink, faPlus, faRotate } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query"
import { useSupportedBanksFromAfterbanks } from "api/Afterbanks.hook"
import { getAfterbanksConsent } from "api/Afterbanks.service"
import { useCuentasBancariasAll } from "api/CuentaBancaria.hook"
import {
  getSyncCuentaBancariaById,
  postBankRevokeAuthorization,
  postCuentaBancaria,
  putCuentaBancariaById,
} from "api/CuentaBancaria.service"
import { GetCuentasBancariasByFiltersDtoProps } from "api/DTOs/GetCuentasBancariasByFiltersDto"
import {
  getEntidadBancariaByIban,
  getEntidadesBancarias,
} from "api/EntidadBancaria.services"
import { EntidadBancariaDtoIPagedEnumerableProps } from "api/Schemas/EntidadBancariaDtoIPagedEnumerable"
import { useUsuarioEmpresaActiva } from "api/Usuario.hook"
import Spinner from "common/components/Spinner"
import SwitchButtons from "common/components/SwitchButtons"
import { Tabs } from "common/components/Tabs"
import CuentasBancariasService from "common/services/CuentasBancariasService"
import notification from "common/utils/notification"
import { requestHandler } from "common/utils/utils"
import { MODAL_STYLES } from "constants/primereactStyles"
import { useAfterbanksNotifications } from "context/afterbankContext"
import { DefaultTFuncReturn } from "i18next"
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"
import { frontendCurrency } from "utils/formatCurrency"
import { FilterMenu } from "./FilterMenu"
import AccountsBoard from "./components/AccountsBoard"
import ModalAutorizar from "./components/ModalAutorizar/ModalAutorizar"
import NewCuentasBancariasForm from "./components/NewCuentaBancariaForm"
import { RevokeAuthorizationModal } from "./components/RevokeAuthorizationModal/RevokeAuthorizationModal"
import {
  BankEntities,
  INITIAL_MODAL_AUTORIZAR_STATE,
  MODAL_TYPE_KEYS,
  MODAL_TYPE_LOADING_SETUP,
  TABLE_BANK_ACCOUNT_COLUMNS,
  getModalTypeSetup,
  getOptions,
  handleRedirect,
  lazyRequest,
  stateAutorizar,
  sumDisponible,
  // sumReal,
  switchButtons,
} from "./constants"
import "./style.scss"

const DeleteModal = lazy(() => import("common/components/Modal/DeleteModal"))
const LoadingModal = lazy(() => import("./components/LoadingModal"))

const Bancos = () => {
  const { t } = useTranslation()
  const isRedirectActive = useRef()
  const history = useHistory()

  const { activeCompany } = useUsuarioEmpresaActiva()
  const { supportedBanks } = useSupportedBanksFromAfterbanks("ES")
  const {
    cuentaBancariaAll,
    cuentaBancariaAllRefetch,
    cuentaBancariaAllIsFetching,
    cuentaBancariaAllIsLoading,
  } = useCuentasBancariasAll({ isRefetchOnMount: "always" })

  const [showDelete, setShowDelete] = useState<{
    id: GetCuentasBancariasByFiltersDtoProps["id"] | null
    status: boolean
  }>({ id: null, status: false })

  const [showModalCreateCuenta, setShowModalCreateCuenta] = useState<{
    values: GetCuentasBancariasByFiltersDtoProps | null
    status: boolean
  }>({
    values: null,
    status: false,
  })
  const [showModalAutorizar, setShowModalAutorizar] = useState<stateAutorizar>(
    INITIAL_MODAL_AUTORIZAR_STATE
  )

  const [modalRevokeAuthorization, setModalRevokeAuthorization] = useState<{
    id: GetCuentasBancariasByFiltersDtoProps["id"]
    show: boolean
  }>({
    id: "",
    show: false,
  })
  const [keyWord, setKeyWord] = useState("")
  const {
    data: dataBank,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["bankListNames", keyWord],
    queryFn: ({ pageParam = 0 }) => {
      return getEntidadesBancarias({
        SkipCount: pageParam,
        Nombre: keyWord,
      })
    },
    getNextPageParam: (lastPage: EntidadBancariaDtoIPagedEnumerableProps) => {
      return lastPage?.currentPage &&
        lastPage?.totalCount &&
        lastPage.currentPage * 10 < lastPage.totalCount
        ? lastPage.currentPage * 10
        : undefined
    },
    initialPageParam: 0,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const gridView =
    localStorage.getItem("tesoreria_cuentasBancarias_defaultView") !== "LIST"
  const [showGrid, setShowGrid] = useState(gridView)
  const [showLoadingModal, setShowLoadingModal] = useState({
    status: false,
    content: MODAL_TYPE_LOADING_SETUP(t)[MODAL_TYPE_KEYS.REDIRECT],
  })

  const isEditView = Boolean(showModalCreateCuenta.values?.id)

  const onFailDelete = useCallback(() => {
    setShowDelete({ id: null, status: !showDelete.status })
  }, [showDelete.status])

  const onFailCreate = () => {
    setShowModalCreateCuenta({
      values: null,
      status: false,
    })
  }

  const onFailSync = useCallback(() => {
    setShowLoadingModal({
      status: false,
      content: MODAL_TYPE_LOADING_SETUP(t)[MODAL_TYPE_KEYS.REDIRECT],
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleOpenCreateModal = async (
    values: GetCuentasBancariasByFiltersDtoProps | null
  ) => {
    if (values?.id && values?.iban) {
      const { data } = await CuentasBancariasService.getCuentaBancariaById(
        values?.id
      )
      const selectedIban = await getEntidadBancariaByIban(values.iban)
      selectedIban?.nombre && setKeyWord(selectedIban.nombre)

      return setShowModalCreateCuenta({
        values: { ...data, entidadBancariaId: selectedIban?.id },
        status: !showModalCreateCuenta.status,
      })
    }
    setShowModalCreateCuenta({ values, status: !showModalCreateCuenta.status })
  }

  const onSuccessCreate = () => {
    setShowModalCreateCuenta({
      values: null,
      status: false,
    })
    cuentaBancariaAllRefetch()
  }

  const onSuccessDelete = () => {
    setShowDelete({ id: null, status: !showDelete.status })
    cuentaBancariaAllRefetch()
  }

  const handleCreateCuentaBancaria = async (values: $TSMigrationFixMe) => {
    requestHandler(
      values,
      postCuentaBancaria,
      onSuccessCreate,
      `${t("bancos.msg.createExit")}`,
      null,
      onFailCreate,
      null
    )
  }

  const handleDeleteCuentaBancaria = async () => {
    if (showDelete.id) {
      requestHandler(
        showDelete.id,
        CuentasBancariasService.deleteCuentaBancaria,
        onSuccessDelete,
        `${t("bancos.msg.deleteExit")}`,
        null,
        onFailDelete,
        null
      )
    }
  }

  const handleUpdateCuentaBancaria = async (values: $TSMigrationFixMe) => {
    if (values) {
      requestHandler(
        {
          cuentaBancaria: {
            cuentaContableEmpresaId: values?.cuentaContableEmpresaId,
            id: values?.id,
            identificadorAcreedor: values?.identificadorAcreedor,
            identificadorPresentador: values?.identificadorPresentador,
            nombre: values?.nombre,
          },
          id: showModalCreateCuenta.values?.id,
        },
        putCuentaBancariaById,
        onSuccessCreate,
        `${t("bancos.msg.createExit")}`,
        null,
        onFailCreate,
        null
      )
    }
  }

  const handleRedirectToDetail = (
    cuentaBancaria: Partial<GetCuentasBancariasByFiltersDtoProps>
  ) => {
    const { id } = cuentaBancaria
    history.push({
      pathname: `${ROUTES.BANCOS}/${id}`,
    })
  }

  const handleOpenDeleteModal = (
    id: GetCuentasBancariasByFiltersDtoProps["id"] | null
  ) => {
    setShowDelete({ id, status: !showDelete.status })
  }

  const onSuccessRedirect = (_: $TSMigrationFixMe, url: $TSMigrationFixMe) => {
    handleRedirect(url)
  }

  const handleOpenModalAutorizar = ({
    entidadBancariaNombre,
    id,
    iban,
    bic,
  }: $TSMigrationFixMe) => {
    setShowModalAutorizar((prevState: stateAutorizar) => ({
      ...prevState,
      status: true,
      id,
      entidadBancariaNombre,
      iban,
      bic,
    }))
  }

  const handleOpenRevokeAuthorizationModal = (id?: string) => {
    setModalRevokeAuthorization({
      id,
      show: !modalRevokeAuthorization.show,
    })
  }

  const handleImportarMovimientos = async () => {
    setShowLoadingModal({
      ...showLoadingModal,
      ...getModalTypeSetup(showModalAutorizar.entidadBancariaNombre, t),
      status: true,
    })

    // @ts-expect-error TS(2322): Type 'true' is not assignable to type 'undefined'.
    isRedirectActive.current = true

    if (isRedirectActive.current) {
      const PSD2Account = supportedBanks?.find(
        (b) => b.swift === showModalAutorizar.bic
      )

      if (!PSD2Account) {
        notification.errorServidor({ body: `${t("errors.unsupportedBank")}` })
        onFailSync()
        return
      }

      const generateLinkRequest = await lazyRequest(PSD2Account.service)

      try {
        await getAfterbanksConsent({
          Iban: showModalAutorizar.iban,
          Service: generateLinkRequest,
          UrlRedirect: `${import.meta.env.REACT_APP_PUBLIC_URL}${
            ROUTES.BANCOS
          }`,
          UserIdentityId: activeCompany.id,
          NifCompany: activeCompany.nif,
          IsTransactionSyncEnabled: showModalAutorizar.movimientos,
        }).then((resp) => {
          if (resp.follow) {
            onSuccessRedirect(null, resp.follow)
          }
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    }
  }

  const { addNotification, getNotificationIdCompleted } =
    useAfterbanksNotifications()

  const handleRefreshList = async (
    rowData: GetCuentasBancariasByFiltersDtoProps
  ) => {
    if (!rowData?.id || !rowData?.idExterno) return
    addNotification({
      id: rowData?.id,
      idExterno: rowData?.idExterno,
    })
    try {
      await getSyncCuentaBancariaById(rowData.id)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      getNotificationIdCompleted(rowData.id)
    }
  }
  const onRenderImportarButton = (
    rowData: GetCuentasBancariasByFiltersDtoProps
  ) => {
    return (
      <div className='tw-flex tw-justify-center'>
        {rowData.necesitaValidacion ? (
          <ButtonERP
            customize={{
              size: "md",
              type: "primary",
            }}
            onClick={() => {
              rowData?.identificadorExterno && handleOpenModalAutorizar(rowData)
            }}
            className={`${
              !rowData?.identificadorExterno
                ? "!tw-opacity-20 !tw-cursor-not-allowed"
                : ""
            } !tw-w-40 tw-justify-center tw-flex`}
            label={`${t("bancos.table.autorizeAccess")}`}
            icon={<FontAwesomeIcon icon={faLink} />}
            tooltip={
              !rowData?.identificadorExterno
                ? `${t("bancos.bankCard.externalIdDisabled")}`
                : undefined
            }
            tooltipOptions={{
              position: "left",
            }}
            unstyled
          />
        ) : (
          <ButtonERP
            customize={{
              size: "md",
              type: "white",
            }}
            className='!tw-w-40 tw-flex tw-justify-center'
            onClick={() => handleRefreshList(rowData)}
            loading={
              rowData?.id ? getNotificationIdCompleted(rowData.id) : false
            }
            disabled={
              rowData?.id ? getNotificationIdCompleted(rowData.id) : false
            }
            label={`${t("bancos.idView.updateButton")}`}
            icon={<FontAwesomeIcon icon={faRotate} />}
            unstyled
          />
        )}
      </div>
    )
  }

  const handleReglasConciliacion = useCallback(
    (iban: string) => {
      history.push({
        pathname: `${ROUTES.REGLAS_CONCILIACION}/${activeCompany.nif}`,
        search: `?iban=${iban}`,
      })
    },
    [history, activeCompany?.nif]
  )
  const handlePropuestasConciliacion = useCallback(
    (id: string, iban?: string) => {
      history.push({
        pathname: `${ROUTES.PROPUESTAS_CONCILIACION}/${id}`,
        search: `?iban=${encodeURIComponent(
          iban ?? ""
        )}&nifTitular=${encodeURIComponent(activeCompany.nif ?? "")}`,
      })
    },
    [activeCompany.nif, history]
  )

  const onRenderActionIcons = (
    rowData: GetCuentasBancariasByFiltersDtoProps
  ) => {
    return (
      <OptionsMenuStorybook
        id={rowData.id}
        items={getOptions(
          handleOpenDeleteModal,
          handleOpenCreateModal,
          handleOpenRevokeAuthorizationModal,
          handleRefreshList,
          getNotificationIdCompleted,
          handleReglasConciliacion,
          handlePropuestasConciliacion,
          false,
          rowData,
          t
        )}
        position='right'
      />
    )
  }

  const handleCloseLoadingModal = () => {
    // @ts-expect-error TS(2322): Type 'false' is not assignable to type 'undefined'...
    isRedirectActive.current = false
    setShowLoadingModal({
      status: false,
      content: MODAL_TYPE_LOADING_SETUP(t)[MODAL_TYPE_KEYS.REDIRECT],
    })
  }

  const [bancosFiltered, setBancosFiltered] = useState<
    GetCuentasBancariasByFiltersDtoProps[]
  >([])
  const uniqueEntities = cuentaBancariaAll?.items?.reduce((map, item) => {
    if (!map.has(item?.entidadBancaria?.nombre)) {
      map.set(item?.entidadBancaria?.nombre, item.logo)
    }

    return map
  }, new Map())

  const handleRevokeBankConnection = async (
    id: GetCuentasBancariasByFiltersDtoProps["id"]
  ) => {
    try {
      if (!id) {
        return notification.errorServidor({
          body: `${t("notification.bankIdError")}`,
        })
      }
      await postBankRevokeAuthorization(id)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    }
  }

  const entitiesList: BankEntities[] | undefined =
    uniqueEntities &&
    Array.from(uniqueEntities).map(([entidad, logo]) => ({
      entidad,
      logo,
    }))

  useEffect(() => {
    if (cuentaBancariaAllIsFetching) {
      return setBancosFiltered([])
    }
    setBancosFiltered(cuentaBancariaAll?.items ?? [])
  }, [cuentaBancariaAll, cuentaBancariaAllIsFetching])

  const TabChild = ({
    icon,
    label,
  }: {
    icon?: IconName
    label?: DefaultTFuncReturn
  }) => (
    <div className='tw-flex tw-gap-2 tw-items-center tw-justify-center tw-w-40'>
      {icon && (
        <FontAwesomeIcon
          icon={["fal", icon]}
          className='tw-h-5'
        />
      )}
      {label && label}
    </div>
  )

  const onRowClick = async ({
    data,
  }: {
    data: Partial<GetCuentasBancariasByFiltersDtoProps>
  }) => {
    if (!data.id) return

    handleRedirectToDetail(data)
  }

  return (
    <section className='tw-h-full'>
      <div className='tw-flex tw-items-center tw-justify-between tw-bg-white tw-px-6 tw-h-20'>
        <Tabs
          defaultTabIndex={0}
          tabs={[
            {
              key: "tab1",
              children: (
                <TabChild
                  icon='landmark'
                  label={t("bancos.tab.accounts")}
                />
              ),
            },
            {
              key: "tab2",
              children: (
                <TabChild
                  icon='credit-card'
                  label={t("bancos.tab.cards")}
                />
              ),
              onClickAction: () => {
                history.push(ROUTES.TARJETAS)
              },
            },
          ]}
        />
        <div className='tw-flex tw-items-center tw-justify-end tw-gap-1 lg:tw-gap-3'>
          <FilterMenu
            uniqueEntities={entitiesList}
            setBancosFiltered={setBancosFiltered}
            listBankAccounts={cuentaBancariaAll?.items}
          />
          <ButtonERP
            onClick={() => handleOpenCreateModal(null)}
            customize={{
              type: "secondary",
              size: "md",
            }}
            icon={<FontAwesomeIcon icon={faPlus} />}
            label={`${t("bancos.add.account")}`}
          />
          <>
            <div className='tw-flex tw-flex-col tw-w-fit tw-px-3 xl:tw-pr-2 tw-border-l tw-border-r'>
              <div className='tw-flex tw-justify-between tw-items-center tw-gap-x-2'>
                <span className='tw-w-full tw-text-base tw-text-left'>
                  {`${t("bancos.idView.labelAvailableBalance")}: `}
                </span>
                <span className='tw-font-medium tw-text-xl'>
                  {frontendCurrency(sumDisponible(bancosFiltered) ?? 0)}
                </span>
              </div>

              {/* <div className='tw-flex tw-justify-between tw-items-center tw-gap-x-2'>
                <span className='tw-w-full tw-text-sm tw-text-left'>
                  {`${t("bancos.idView.labelRealBalance")}: `}
                </span>
                <span className='tw-font-normal tw-text-sm'>
                  {bancosFiltered.some(
                    (account) => account?.saldos?.real !== null
                  )
                    ? frontendCurrency(sumReal(bancosFiltered) ?? 0)
                    : "-"}
                </span>
              </div> */}
            </div>
            <SwitchButtons
              btns={switchButtons(t)}
              onSelect={({ id }: $TSMigrationFixMe) => {
                setShowGrid(id === "GRID")
                localStorage.setItem(
                  "tesoreria_cuentasBancarias_defaultView",
                  id
                )
              }}
              selectedButtonID={showGrid ? "GRID" : "LIST"}
            />
          </>
        </div>
      </div>

      {showGrid ? (
        <AccountsBoard
          cuentasBancarias={bancosFiltered}
          handleRedirectToDetail={handleRedirectToDetail}
          handleOpenDeleteModal={handleOpenDeleteModal}
          handleOpenEditModal={handleOpenCreateModal}
          handleOpenModalAutorizar={handleOpenModalAutorizar}
          handleOpenRevokeAuthorizationModal={
            handleOpenRevokeAuthorizationModal
          }
          handleRefreshList={handleRefreshList}
          getNotificationIdCompleted={getNotificationIdCompleted}
          handleReglasConciliacion={handleReglasConciliacion}
          handlePropuestasConciliacion={handlePropuestasConciliacion}
        />
      ) : (
        <div className='tw-h-[calc(100%-80px)] tw-min-h-0'>
          <TableERP
            columns={TABLE_BANK_ACCOUNT_COLUMNS(
              onRenderImportarButton,
              onRenderActionIcons,
              t
            )}
            className='tw-h-full'
            columnResizeMode='expand'
            value={bancosFiltered}
            showGridlines
            dataKey='id'
            lazy
            scrollable={false}
            onRowClick={onRowClick}
            size='small'
            loading={cuentaBancariaAllIsLoading || cuentaBancariaAllIsFetching}
          />
        </div>
      )}

      <Suspense fallback={<Spinner />}>
        <PrimeReactModal
          visible={showModalCreateCuenta.status}
          className='tw-w-128 tw-rounded-lg tw-py-6 tw-px-6 tw-bg-white'
          baseZIndex={30}
          ptContext={MODAL_STYLES}
          closable={false}
          keepInViewport={false}
          header={
            <div className='tw-flex tw-justify-between tw-items-center tw-mb-6'>
              <p className='tw-text-2xl tw-font-medium tw-text-primary tw-m-0'>
                {`${
                  isEditView
                    ? t("bancos.modal.headerEdit")
                    : t("bancos.modal.headerNew")
                } ${t("bancos.modal.headerAccount")}`}
              </p>
              <ButtonPrimeReact
                className='!tw-p-0 !tw-min-w-0 !tw-bg-white !tw-border-none !tw-text-black'
                onClick={() => {
                  handleOpenCreateModal(null)
                  setKeyWord("")
                }}
              >
                <FontAwesomeIcon
                  icon={["fal", "xmark"]}
                  className='tw-w-5 tw-h-5'
                />
              </ButtonPrimeReact>
            </div>
          }
          onHide={() => handleOpenCreateModal(null)}
        >
          <NewCuentasBancariasForm
            // @ts-expect-error TS(2322): Type '{ createCuentaBancaria: (values: $TSFixMe) => Pro...
            createCuentaBancaria={
              showModalCreateCuenta.values
                ? handleUpdateCuentaBancaria
                : handleCreateCuentaBancaria
            }
            handleOpenCreateModal={handleOpenCreateModal}
            data={showModalCreateCuenta.values}
            isEditView={isEditView}
            t={t}
            setKeyWord={setKeyWord}
            dataBank={dataBank}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
            refetch={refetch}
          />
        </PrimeReactModal>
        <DeleteModal
          visible={showDelete.status}
          onHide={handleOpenDeleteModal}
          size='small'
          iconType='remesas'
          buttonLabel={t("bancos.delete.confirmButton")}
          title={t("bancos.delete.labelTitle")}
          message={t("bancos.delete.msg")}
          onCancel={handleOpenDeleteModal}
          onConfirm={handleDeleteCuentaBancaria}
        />
        <LoadingModal
          showModal={showLoadingModal.status}
          data={showLoadingModal.content}
          onHideModal={handleCloseLoadingModal}
        />
        <ModalAutorizar
          showModalAutorizar={showModalAutorizar}
          setShowModalAutorizar={setShowModalAutorizar}
          handleImportarMovimientos={handleImportarMovimientos}
        />
        <RevokeAuthorizationModal
          isOpen={modalRevokeAuthorization.show}
          onCancel={() =>
            setModalRevokeAuthorization({
              id: "",
              show: !modalRevokeAuthorization.show,
            })
          }
          onConfirm={() =>
            handleRevokeBankConnection(modalRevokeAuthorization.id)
          }
        />
      </Suspense>
    </section>
  )
}

export default Bancos
